import { createRouter as _createRouter, createWebHistory } from "vue-router";
import { useLanguages, useThemeContext } from "@/core/composables";
import { OVERLAY_LOCALES } from "@/core/constants/language-and-currency";
import { getReturnUrlValue } from "@/core/utilities";
import { punchoutCookies } from "@/core/utilities/punchout/punchoutCookies";
import { useUser } from "@/shared/account";
import { useLivePerson, showChatFooter } from "@/shared/live-person";
import { mainRoutes } from "./routes";
import type { RouteRecordName } from "vue-router";

export function createRouter(options: { base: string }) {
  const { base } = options;
  const { isAuthenticated, organization } = useUser();
  const { themeContext } = useThemeContext();
  const { shouldShowChat } = useLivePerson();

  const {
    isCartPage,
    defaultLocale,
    savedLocale,
    savedCartLanguage,
    savedLocaleCopy,
    languagesAvailableExclusivelyInCart,
  } = useLanguages();

  const router = _createRouter({
    routes: mainRoutes,
    history: createWebHistory(base),
  });

  // Prevent browser from auto-restoring scroll position on back/forward navigation.
  if (history.scrollRestoration) {
    history.scrollRestoration = "manual";
  }
  // When the page is restored from bfcache (back/forward navigation after a full-page reload
  // such as a language change), no scripts re-execute so scrollRestoration = "manual" has no
  // effect. The pageshow event fires in both cases; event.persisted === true means bfcache.
  window.addEventListener("pageshow", (event: PageTransitionEvent) => {
    if (event.persisted) {
      window.scrollTo(0, 0);
    }
  });

  // Scroll to top for non-Matcher routes. Matcher handles its own scroll via page watcher.
  router.afterEach((to, from) => {
    if (to.path !== from.path && to.name !== "Matcher") {
      window.scrollTo({ top: 0 });
    }
  });

  router.beforeEach((to, _from, next) => {
    //enable LivePerson on static routes
    //CMS routes are handled in Matcher
    if (to.name !== "Matcher") {
      const showChatStatic = shouldShowChat(true, to.path, undefined);

      showChatFooter.value = showChatStatic;
    }

    //Make useLanguages aware that the next page should accomodate extra languages
    if (
      to.name === "Cart" ||
      (typeof to.name === "string" &&
        !to.path.includes("account") &&
        (to.name.includes("Checkout") || to.name.includes("Quote")))
    ) {
      isCartPage.value = true;
      if (savedCartLanguage.value && savedLocale.value !== savedCartLanguage.value) {
        savedLocale.value = savedCartLanguage.value;
        const localizedSlug =
          savedLocale.value === defaultLocale.value ? to.fullPath : `/${savedLocale.value}${to.fullPath}`;
        const redirectLocation = location.origin + localizedSlug;
        location.href = redirectLocation;
      }
    } else {
      isCartPage.value = false;
      const locationComponents = location.pathname.split("/");
      const restrictedLanguageInUrl = locationComponents.find((item) =>
        languagesAvailableExclusivelyInCart.find((language) => language === item),
      );
      if (!locationComponents.find((item) => OVERLAY_LOCALES.includes(item)) && restrictedLanguageInUrl) {
        savedLocale.value = savedLocaleCopy.value;
        const newSlug = to.fullPath.replace("/" + restrictedLanguageInUrl, "");
        const localizedSlug = savedLocale.value === defaultLocale.value ? newSlug : `/${savedLocale.value}${newSlug}`;
        const redirectLocation = location.origin + localizedSlug;
        location.href = redirectLocation;
      }
    }

    // Protecting routes
    const unauthorizedAccessIsDenied: boolean =
      !isAuthenticated.value &&
      !punchoutCookies.value &&
      !to.meta.public &&
      (to.meta.requiresAuth || !themeContext.value.storeSettings.anonymousUsersAllowed);

    if (unauthorizedAccessIsDenied) {
      return next({
        name: "SignIn",
        // save the location we were at to come back later
        query: { returnUrl: to.fullPath },
      });
    }

    // Protecting company routes
    if (to.meta.requiresOrganization && !organization.value) {
      return next({ name: "Account" });
    }

    // Make Dashboard the default Home page for authorized users
    if (
      isAuthenticated.value &&
      !punchoutCookies.value &&
      Array<RouteRecordName>(
        "Home",
        "SignIn",
        "SignUp",
        "ForgotPassword",
        "ResetPasswordInitial",
        "ResetPassword",
        "SetPassword",
        "ConfirmInvitation",
      ).includes(to.name!)
    ) {
      return next(getReturnUrlValue() || { path: "/" });
    }

    // Punchout routes restriction
    const punchoutBlockedRoutes = [
      "/sign-in",
      "/sign-up",
      "/forgot-password",
      "/reset-password",
      "/forgot-id",
      "/checkout",
    ];

    const isBlockedPath = punchoutBlockedRoutes.includes(to.path);

    if (punchoutCookies.value && isBlockedPath) {
      return next({ path: "/404" }); // Redirect to not found page
    }

    return next();
  });

  return router;
}
